import { UNCATEGORIZED_KEY } from "./knowledge-grouping";

/** English source string for the catch-all scope; rendered through i18next `t()`. */
const UNCATEGORIZED_LABEL = "Uncategorized";

export interface HomeKnowledgeScopeGroup {
  key: string;
  label: string;
  isUncategorized: boolean;
  items: readonly unknown[];
}

export interface HomeKnowledgeScope {
  key: string;
  label: string;
  books: number;
  notes: number;
  chats: number;
  total: number;
}

export interface HomeKnowledgeScopeOption {
  value: string;
  label: string;
}

export function buildHomeKnowledgeScopes(
  knowledgeNames: readonly string[],
  courseGroups: readonly HomeKnowledgeScopeGroup[],
  noteGroups: readonly HomeKnowledgeScopeGroup[],
  sessionGroups: readonly HomeKnowledgeScopeGroup[],
): HomeKnowledgeScope[] {
  const names = new Set(knowledgeNames);
  for (const groups of [courseGroups, noteGroups, sessionGroups]) {
    for (const group of groups) {
      if (!group.isUncategorized) names.add(group.label);
    }
  }

  const countFor = (
    groups: readonly HomeKnowledgeScopeGroup[],
    key: string,
  ) => groups.find((group) => group.key === key)?.items.length ?? 0;
  const buildScope = (key: string, label: string): HomeKnowledgeScope => {
    const books = countFor(courseGroups, key);
    const notes = countFor(noteGroups, key);
    const chats = countFor(sessionGroups, key);
    return {
      key,
      label,
      books,
      notes,
      chats,
      total: books + notes + chats,
    };
  };

  const scopes = Array.from(names)
    .filter((name) => name.length > 0)
    .sort((left, right) => left.localeCompare(right, "ko"))
    .map((name) => buildScope(name, name));
  const uncategorized = buildScope(UNCATEGORIZED_KEY, UNCATEGORIZED_LABEL);
  if (uncategorized.total > 0) scopes.push(uncategorized);
  return scopes;
}

export function toHomeKnowledgeScopeOptions(
  scopes: readonly HomeKnowledgeScope[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): HomeKnowledgeScopeOption[] {
  return scopes.map((scope) => ({
    value: scope.key,
    label: t("{{label}} — {{books}} books · {{notes}} notes · {{chats}} chats", {
      label: scope.label === UNCATEGORIZED_LABEL ? t(UNCATEGORIZED_LABEL) : scope.label,
      books: scope.books,
      notes: scope.notes,
      chats: scope.chats,
    }),
  }));
}

export function toActiveKnowledge(scope: string | null): string | null {
  return scope === UNCATEGORIZED_KEY ? null : scope;
}
